import {Component, EventEmitter, Input, Output} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';

import {NewPoll, Poll} from '../../_types/polls';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {debounceTime, take} from 'rxjs';
import {CdkDragDrop, moveItemInArray} from "@angular/cdk/drag-drop";

import {MatCheckboxChange} from "@angular/material/checkbox";

import {removeNullUndefinedSpecificKeyFieldsRecursive} from '../../_utils/remove-null-fields';
import {PollsService} from '../../_services/polls.service';
import {mergeDeep} from '../../_utils/utils';
import {SnackbarService} from '../../_services/snackbar.service';


@Component({
    selector: 'app-create-poll',
    templateUrl: './create-poll.component.html',
    styleUrl: './create-poll.component.scss'
})
export class CreatePollComponent {
    identifiers: string[] = []
    formGroup: FormGroup = this.fb.group({
        text: this.fb.control(''),
        description: this.fb.control(''),
        end_at: this.fb.control(null),
        scale: this.fb.control(false),
        matrix_exclusive: this.fb.control(false),
        shuffle_questions: this.fb.control(false),
        questions: this.fb.array([
            this.newQuestion(),
        ])
    });
    @Output() isValidPollData: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() pollData: EventEmitter<NewPoll> = new EventEmitter<NewPoll>();
    @Output() pollUpdated: EventEmitter<Poll> = new EventEmitter<Poll>();
    @Output() pollUpdateCanceled: EventEmitter<void> = new EventEmitter<void>();
    initialPoll?: Poll
    editMode: boolean = false;
    sending: boolean = false;
    scaleAmount: number = 5;
    // Scale numbers needs to start at the number of questions and have at least the number of questions in numbers
    scaleNumbers: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

    // To do this the scale numbers need to change when a question is added or removed or when matrix_exclusive is toggled on or off

    constructor(
        private readonly fb: FormBuilder,
        private readonly pollsService: PollsService,
        private readonly snackbarService: SnackbarService,
    ) {
        this.formGroup.statusChanges.pipe(takeUntilDestroyed()).subscribe(res => {
            this.isValidPollData.emit(res === 'VALID' || res === 'DISABLED');
        });
        this.formGroup.valueChanges.pipe(
            debounceTime(200),
            takeUntilDestroyed()
        ).subscribe(() => {
            this.pollData.emit(removeNullUndefinedSpecificKeyFieldsRecursive(this.formGroup.value, ['id']));
        });

        this.formGroup.get('matrix_exclusive')!.valueChanges
            .subscribe(r => {
                this.updateScaleNumbers();
            })
    }


    @Input() set initialPollData(value: Poll | undefined) {
        if (!value) return;
        this.initialPoll = value;
        this.removeQuestion(0);
        Array.from({length: value.questions.length}, (): void => this.addQuestion(false));
        value.questions.forEach((question, i): void => {
            Array.from({length: question.options.length}, (): void => this.addOption(i));
            question.options.forEach((option, j): void => {
                Array.from({length: option.nested_options.length}, (): void => this.addNestedOption(i, j, false));
            })
        })

        this.formGroup.patchValue(value);
        this.questions().controls.forEach((question, i): void => {
            question.get('display_condition_options_identifiers')?.patchValue(value.questions[i].display_condition_options_identifiers_list)
            question.get('display_condition_options_identifiers')?.disable();
            question.get('max_responses')?.disable();
            this.options(i).controls.forEach((option, j): void => {
                option.get('max_nested_responses')?.disable();
            })
        })
        this.formGroup.updateValueAndValidity();
        this.disableAllCheckboxes(this.formGroup);
        this.editMode = true;
    };

    get isScale(): boolean {
        return this.formGroup.get('scale')?.value || false
    }

    get isMatrixExclusive(): boolean {
        return this.formGroup.get('matrix_exclusive')?.value || false
    }

    questions(): FormArray {
        return this.formGroup.get('questions') as FormArray;
    }

    question(questionIndex: number): AbstractControl {
        return this.questions().at(questionIndex)
    }

    getOption(questionIndex: number, optionIndex: number): AbstractControl {
        return this.options(questionIndex).at(optionIndex)
    }

    options(questionIndex: number): FormArray {
        return this.questions()
            .at(questionIndex)
            .get('options') as FormArray;
    }

    nestedOptions(questionIndex: number, optionIndex: number): FormArray {
        return this.options(questionIndex)
            .at(optionIndex)
            .get('nested_options') as FormArray;
    }

    guidGenerator(): any {
        const S4: () => string = function (): string {
            return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
        };
        let guid: string = (S4() + S4() + "-" + S4() + "-" + S4() + "-" + S4() + "-" + S4() + S4() + S4());

        if (this.identifiers.includes(guid)) {
            return this.guidGenerator();
        } else {
            this.identifiers.push(guid)
            return guid
        }
    }

    removeQuestion(i: number): void {
        this.questions().removeAt(i);
        this.updateScaleNumbers();
    }

    addQuestion(initOptions: boolean = true): void {
        this.questions().push(this.newQuestion(initOptions));
        this.updateScaleNumbers();
    }

    removeOption(questionId: number, i: number): void {
        this.options(questionId).removeAt(i);
    }

    addOption(questionId: number): void {
        this.options(questionId).push(this.newOption());
    }

    likertScale(event: MatCheckboxChange): void {
        for (let index: number = 0; index < this.questions().length; index++) {
            this.removeQuestion(index);
        }

        if (event.checked) {
            this.formGroup.patchValue({
                scale: true
            })
            this.addQuestion()
        } else {
            this.formGroup.patchValue({
                scale: false,
                matrix_exclusive: false,
                shuffle_questions: false
            })
            this.addQuestion(true)
        }
    }

    scaleUpdate(): void {
        for (let index: number = 0; index < this.questions().length; index++) {
            this.options(index).clear();
            for (let i: number = 1; i < this.scaleAmount + 1; i++) {
                this.options(index).push(this.newOption(JSON.stringify(i)));
            }
        }
    }

    removeNestedOption(questionId: number, optionId: number, i: number): void {
        this.nestedOptions(questionId, optionId).removeAt(i);
    }

    addNestedOption(questionId: number, optionId: number, firstAdd?: boolean): void {
        this.nestedOptions(questionId, optionId).push(this.newNestedOption());
        if (firstAdd) {
            // 2 is minimum number of options
            this.nestedOptions(questionId, optionId).push(this.newNestedOption());
        }
    }

    removeNestedPoll(questionId: number, optionId: number): void {
        this.nestedOptions(questionId, optionId).clear();
    }

    drop(event: CdkDragDrop<string[]>, questionId: number): void {
        moveItemInArray(this.options(questionId).controls, event.previousIndex, event.currentIndex);
    }

    dropNested(event: CdkDragDrop<string[]>, questionId: number, optionId: number): void {
        moveItemInArray(this.nestedOptions(questionId, optionId).controls, event.previousIndex, event.currentIndex);
    }

    updatePoll(): boolean {
        this.sending = true;
        const updatedPoll = mergeDeep(this.initialPoll, this.formGroup.value);
        const updatedPollOrg = JSON.parse(JSON.stringify(updatedPoll));
        ['author', 'post', 'total_votes', 'user_vote'].forEach(key => {
            if (key in updatedPoll) {
                delete updatedPoll[key];
            }
        });

        this.pollsService.editPoll(updatedPoll).pipe(take(1))
            .subscribe({
                next: (): void => {
                    this.sending = false;
                    this.snackbarService.openSuccessSnackBar('Poll has been successfully updated.');
                    this.pollUpdated.emit(updatedPollOrg);
                },
                error: (err: string): void => {
                    this.sending = false;
                    this.snackbarService.openErrorSnackBar(err);
                }
            })
        return false;
    }

    updateScaleNumbers(): void {

        if (this.isMatrixExclusive) {
            this.scaleNumbers = [];

            for (let i: number = 1; i <= (this.questions().length < 10 ? 10 : this.questions().length); i++) {
                // The minimum amount of options is 10 but if there are more then 10 questions then the numbers must at least match.
                this.scaleNumbers.push(i)
            }
            // You cannot have numbers below the number of questions
            this.scaleNumbers = this.scaleNumbers?.filter(
                (num): boolean => num >= this.questions().length
            );

        } else if (JSON.stringify(this.scaleNumbers) != JSON.stringify([1, 2, 3, 4, 5, 6, 7, 8, 9, 10])) {
            // Reset if isMatrixExclusive has been removed. But don't reset if not needed
            this.scaleNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
        }
        if (this.isScale) {
            if (!this.scaleNumbers.includes(this.scaleAmount)) {
                // if the current selected value has been removed then select a new one
                this.scaleAmount = this.scaleNumbers[0];
            }
            this.scaleUpdate()
        }
    }

    private newQuestion(initOptions: boolean = true): FormGroup {
        let fbArray: FormGroup<any>[] = initOptions ? [this.newOption(), this.newOption()] : [];
        if (this.formGroup?.value?.scale) {
            fbArray = [];
            for (let i = 1; i < this.scaleAmount + 1; i++) {
                fbArray.push(this.newOption(JSON.stringify(i)))
            }
        }
        return this.fb.group({
            id: this.fb.control(null),
            text: this.fb.control('', Validators.required),
            multiple_choices: this.fb.control(false),
            randomize_options: this.fb.control(false),
            display_condition_options_identifiers: this.fb.control([]),
            max_responses: this.fb.control(null, Validators.min(2)),
            options: this.fb.array(fbArray)
        });
    }

    private newOption(defaultText?: string): FormGroup {
        return this.fb.group({
            id: this.fb.control(null),
            text: this.fb.control(defaultText || '', Validators.required),
            description: this.fb.control(''),
            not_applicable: this.fb.control(false),
            fixed: this.fb.control(false),
            randomize_nested_options: this.fb.control(false),
            nested_multiple_choices: this.fb.control(false),
            max_nested_responses: this.fb.control(null, Validators.min(2)),
            nested_question_title: this.fb.control(''),
            nested_options: this.fb.array([]),
            identifier: this.fb.control(this.guidGenerator()),
        });
    }

    private newNestedOption(): FormGroup {
        return this.fb.group({
            id: this.fb.control(null),
            text: this.fb.control('', Validators.required),
            description: this.fb.control(''),
            not_applicable: this.fb.control(false),
            fixed: this.fb.control(false),
            identifier: this.fb.control(this.guidGenerator()),
        });
    }

    private disableAllCheckboxes(formGroup: FormGroup | FormArray): void {
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);

            if (control instanceof FormGroup || control instanceof FormArray) {
                this.disableAllCheckboxes(control);
            } else if (control instanceof FormControl && control.value !== null && typeof control.value === 'boolean') {
                control.disable();
            }
        });
    }
}
